import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { uploadService } from '@/services/upload.service';
import { codeMessage } from '@/utils/upload';
import { Upload } from '@/types/upload';

/** The rows an import could not take, and why: the first 200. */
const UploadIssues: React.FC<{ upload: Upload }> = ({ upload }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['uploads', upload.workspace, upload.id, 'issues'],
    queryFn: () => uploadService.issues(upload.workspace, upload.id),
    retry: false,
  });
  if (isLoading) return <p className="text-xs text-gn-muted">…</p>;
  if (isError) return <p className="text-xs text-gn-danger">{t('uploads.issues_failed')}</p>;
  if (!data || data.length === 0) return <p className="text-xs text-gn-muted">{t('uploads.no_issues')}</p>;
  return (
    <div className="max-h-48 overflow-y-auto border border-gn-light rounded">
      <table className="w-full text-xs">
        <thead className="bg-gn-primary sticky top-0">
          <tr>
            <th className="px-2 py-1 text-right font-medium text-gn-muted w-16">{t('uploads.row')}</th>
            <th className="px-2 py-1 text-left font-medium text-gn-muted">{t('uploads.problem')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((i, n) => (
            <tr key={n} className="border-t border-gn-light">
              <td className="px-2 py-1 text-right tabular-nums text-gn-muted">{i.row ?? '–'}</td>
              <td className="px-2 py-1 text-gn-primary">
                {codeMessage(i.code) ?? i.message ?? i.code}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {upload.issueCount > data.length && (
        <p className="px-2 py-1 text-[11px] text-gn-muted">{t('uploads.more_issues', { count: upload.issueCount - data.length })}</p>
      )}
    </div>
  );
};

export default UploadIssues;
